import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { addMinutes, sub } from "date-fns";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { formatDate } from "utils/formatDate";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { DATE_FORMATS } from "../../constants";
import { instancesActions } from "./slices";
import {
  AddFirewallParams,
  AddFirewallResponse,
  AttachVolumeParams,
  AttachVolumeResponse,
  ConfirmResizeInstanceParams,
  ConfirmResizeInstanceResponse,
  CreateInstanceParams,
  CreateInstanceResponse,
  CreateInterfaceParams,
  CreateInterfaceResponse,
  DeleteInstanceParams,
  DeleteInstanceResponse,
  DeleteInterfaceParams,
  DeleteInterfaceResponse,
  DetachVolumeParams,
  DetachVolumeResponse,
  GetFlavorParams,
  GetFlavorResponse,
  GetFlavorsParams,
  GetFlavorsResponse,
  GetImageParams,
  GetImageResponse,
  GetImagesParams,
  GetImagesResponse,
  GetInstanceActionLogsParams,
  GetInstanceActionLogsResponse,
  GetInstanceFirewallsParams,
  GetInstanceFirewallsResponse,
  GetInstanceLimitsParams,
  GetInstanceLimitsResponse,
  GetInstanceLogParams,
  GetInstanceLogResponse,
  GetInstanceMetricParams,
  GetInstanceMetricResponse,
  GetInstanceParams,
  GetInstanceResponse,
  GetInstancesParams,
  GetInstancesResponse,
  GetInterfaceParams,
  GetInterfaceResponse,
  GetInterfacesParams,
  GetInterfacesResponse,
  GetRemoteConsoleURLParams,
  GetRemoteConsoleURLResponse,
  RemoveFirewallParams,
  RemoveFirewallResponse,
  ResizeInstanceParams,
  ResizeInstanceResponse,
  RestartInstanceParams,
  RevertResizeInstanceParams,
  RevertResizeInstanceResponse,
  ShelveInstanceParams,
  StartInstanceParams,
  StopInstanceParams,
  UpdateInstanceParams,
  UpdateInstanceResponse
} from "./types";

const gothamWayneApi = `gotham-wayne-tower/method/`;

export function* getInstanceSaga(
  action: PayloadAction<GetInstanceParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetInstanceResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInstanceSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get virtual machine data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInstancesSaga(
  action: PayloadAction<GetInstancesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetInstancesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/instances`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInstancesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInstancesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get virtual machines data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createInstanceSaga(
  action: PayloadAction<CreateInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateInstanceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances`,
      data
    );
    yield put(instancesActions.createInstanceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title:
          "Request to create virtual machine has been successfully sent. It will take a few moments.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.createInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateInstanceSaga(
  action: PayloadAction<UpdateInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateInstanceResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}`,
      data
    );
    yield put(instancesActions.updateInstanceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Virtual machine has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.updateInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* resizeInstanceSaga(
  action: PayloadAction<ResizeInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to resize virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, flavorId } = action.payload;
    const response: AxiosResponse<ResizeInstanceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/resize/${flavorId}`
    );
    yield put(instancesActions.resizeInstanceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to resize virtual machine has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.resizeInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to resize virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* confirmResizeInstanceSaga(
  action: PayloadAction<ConfirmResizeInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Confirming the virtual machine resize...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<ConfirmResizeInstanceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/resize/confirm`
    );
    yield put(instancesActions.confirmResizeInstanceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Virtual machine resize has been successfully confirmed.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.confirmResizeInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to confirm virtual machine resize",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* revertResizeInstanceSaga(
  action: PayloadAction<RevertResizeInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Reverting the virtual machine resize...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<RevertResizeInstanceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/resize/revert`
    );
    yield put(instancesActions.revertResizeInstanceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Virtual machine resize has been successfully reverted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.revertResizeInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to revert virtual machine resize",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteInstanceSaga(
  action: PayloadAction<DeleteInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteInstanceResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}`
    );
    yield put(instancesActions.deleteInstanceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title:
          "Request to delete virtual machine has been successfully sent. It will take a few moments",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.deleteInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* startInstanceSaga(
  action: PayloadAction<StartInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Starting the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/start`
    );
    yield put(instancesActions.startInstanceSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Request to start virtual machine has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.startInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to start virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* restartInstanceSaga(
  action: PayloadAction<RestartInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Rebooting the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/restart`,
      null,
      {
        params: {
          type: action.payload.type
        }
      }
    );
    yield put(instancesActions.restartInstanceSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Request to reboot virtual machine has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.restartInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to reboot virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}
// status SHUTOFF (billable)
export function* stopInstanceSaga(
  action: PayloadAction<StopInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Shutoff the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/stop`
    );
    yield put(instancesActions.stopInstanceSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Request to shutoff virtual machine has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.stopInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to shutoff virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// status SHELVED_OFFLOADED (not billable)
export function* shelveInstanceSaga(
  action: PayloadAction<ShelveInstanceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Shelving the virtual machine...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${id}/shelve`
    );
    yield put(instancesActions.shelveInstanceSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Request to shelve virtual machine has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.shelveInstanceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to shelve virtual machine",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFlavorSaga(
  action: PayloadAction<GetFlavorParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetFlavorResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/flavors/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getFlavorSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getFlavorFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get flavor data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFlavorsSaga(
  action: PayloadAction<GetFlavorsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, type } = action.payload;
    const response: AxiosResponse<GetFlavorsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/flavors?type=${type}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getFlavorsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getFlavorsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get flavors data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getImageSaga(
  action: PayloadAction<GetImageParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetImageResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/images/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getImageSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getImageFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get image data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getImagesSaga(
  action: PayloadAction<GetImagesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetImagesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/images`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getImagesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getImagesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get images data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInterfaceSaga(
  action: PayloadAction<GetInterfaceParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId, id } = action.payload;
    const response: AxiosResponse<GetInterfaceResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/interfaces/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInterfaceSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get interface data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInterfacesSaga(
  action: PayloadAction<GetInterfacesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId } = action.payload;
    const response: AxiosResponse<GetInterfacesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/devices/${instanceId}/interfaces`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInterfacesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInterfacesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get interfaces data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createInterfaceSaga(
  action: PayloadAction<CreateInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, instanceId, data } = action.payload;
    const response: AxiosResponse<CreateInterfaceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/interfaces`,
      data
    );
    yield put(instancesActions.createInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Interface has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.createInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteInterfaceSaga(
  action: PayloadAction<DeleteInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, instanceId, id } = action.payload;
    const response: AxiosResponse<DeleteInterfaceResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/interfaces/${id}`
    );
    yield put(instancesActions.deleteInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete interface has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.deleteInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInstanceLimitsSaga(
  action: PayloadAction<GetInstanceLimitsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetInstanceLimitsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/compute/limits`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInstanceLimitsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInstanceLimitsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get instance limits data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* attachVolumeSaga(
  action: PayloadAction<AttachVolumeParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Attaching the volume...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, instanceId, volumeId } = action.payload;
    const response: AxiosResponse<AttachVolumeResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/attach-volume/${volumeId}`
    );
    yield put(instancesActions.attachVolumeSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Volume has been successfully attached.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.attachVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to attach volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* detachVolumeSaga(
  action: PayloadAction<DetachVolumeParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to detach volume...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, instanceId, volumeId } = action.payload;
    const response: AxiosResponse<DetachVolumeResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/detach-volume/${volumeId}`
    );
    yield put(instancesActions.detachVolumeSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to detach volume has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.detachVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request sto detach volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* addFirewallSaga(
  action: PayloadAction<AddFirewallParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Adding the firewall...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, instanceId, firewallId } = action.payload;
    const response: AxiosResponse<AddFirewallResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/firewalls/${firewallId}`
    );
    yield put(instancesActions.addFirewallSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Firewall has been successfully added.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.addFirewallFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to add firewall",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* removeFirewallSaga(
  action: PayloadAction<RemoveFirewallParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Removing the firewall...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, instanceId, firewallId } = action.payload;
    const response: AxiosResponse<RemoveFirewallResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/firewalls/${firewallId}`
    );
    yield put(instancesActions.removeFirewallSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Firewall has been successfully removed.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      instancesActions.removeFirewallFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to remove firewall",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInstanceFirewallsSaga(
  action: PayloadAction<GetInstanceFirewallsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId } = action.payload;
    const response: AxiosResponse<GetInstanceFirewallsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/firewalls`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInstanceFirewallsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInstanceFirewallsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get virtual machine firewalls data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getRemoteConsoleURLSaga(
  action: PayloadAction<GetRemoteConsoleURLParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId } = action.payload;
    const response: AxiosResponse<GetRemoteConsoleURLResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/remote-console`
    );
    yield put(instancesActions.getRemoteConsoleURLSuccess(response.data));
    window.open(response.data.url, "_blank");
  } catch (e) {
    yield put(
      instancesActions.getRemoteConsoleURLFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get remote console URL",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInstanceLogSaga(
  action: PayloadAction<GetInstanceLogParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId, logLength } = action.payload;
    const response: AxiosResponse<GetInstanceLogResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/logs`,
      { params: { length: logLength } }
    );
    yield put(instancesActions.getInstanceLogSuccess(response.data));
  } catch (e) {
    yield put(
      instancesActions.getInstanceLogFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get virtual machine log",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInstanceActionLogsSaga(
  action: PayloadAction<GetInstanceActionLogsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId } = action.payload;
    const response: AxiosResponse<GetInstanceActionLogsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/instances/${instanceId}/action-logs`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInstanceActionLogsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInstanceActionLogsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get virtual machine action logs",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInstanceMetricsSaga(
  action: PayloadAction<GetInstanceMetricParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, instanceId } = action.payload;

    const startDate = sub(new Date(), { hours: 1 });

    const response: AxiosResponse<GetInstanceMetricResponse> = yield call(
      axiosInstance.get,
      `${gothamWayneApi}metrics/regions/${regionId}/projects/${projectId}/instances/${instanceId}`,
      {
        params: {
          startDate: formatDate(
            addMinutes(startDate, startDate.getTimezoneOffset()),
            DATE_FORMATS.ISO_DATETIME
          )
        }
      }
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(instancesActions.getInstanceMetricsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      instancesActions.getInstanceMetricsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get virtual machine metrics",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(instancesActions.getInstanceRequest, getInstanceSaga),
    takeLeading(instancesActions.getInstancesRequest, getInstancesSaga),
    takeEvery(instancesActions.createInstanceRequest, createInstanceSaga),
    takeEvery(instancesActions.updateInstanceRequest, updateInstanceSaga),
    takeEvery(instancesActions.resizeInstanceRequest, resizeInstanceSaga),
    takeEvery(
      instancesActions.confirmResizeInstanceRequest,
      confirmResizeInstanceSaga
    ),
    takeEvery(
      instancesActions.revertResizeInstanceRequest,
      revertResizeInstanceSaga
    ),
    takeEvery(instancesActions.deleteInstanceRequest, deleteInstanceSaga),
    takeLeading(instancesActions.getFlavorRequest, getFlavorSaga),
    takeLeading(instancesActions.getFlavorsRequest, getFlavorsSaga),
    takeLeading(instancesActions.getImageRequest, getImageSaga),
    takeLeading(instancesActions.getImagesRequest, getImagesSaga),
    takeEvery(instancesActions.startInstanceRequest, startInstanceSaga),
    takeEvery(instancesActions.restartInstanceRequest, restartInstanceSaga),
    takeEvery(instancesActions.stopInstanceRequest, stopInstanceSaga),
    takeEvery(instancesActions.shelveInstanceRequest, shelveInstanceSaga),
    takeLeading(instancesActions.getInterfaceRequest, getInterfaceSaga),
    takeLeading(instancesActions.getInterfacesRequest, getInterfacesSaga),
    takeEvery(instancesActions.createInterfaceRequest, createInterfaceSaga),
    takeEvery(instancesActions.deleteInterfaceRequest, deleteInterfaceSaga),
    takeLeading(
      instancesActions.getInstanceLimitsRequest,
      getInstanceLimitsSaga
    ),
    takeEvery(instancesActions.attachVolumeRequest, attachVolumeSaga),
    takeEvery(instancesActions.detachVolumeRequest, detachVolumeSaga),
    takeEvery(instancesActions.addFirewallRequest, addFirewallSaga),
    takeEvery(instancesActions.removeFirewallRequest, removeFirewallSaga),
    takeLeading(
      instancesActions.getInstanceFirewallsRequest,
      getInstanceFirewallsSaga
    ),
    takeLeading(
      instancesActions.getRemoteConsoleURLRequest,
      getRemoteConsoleURLSaga
    ),
    takeLeading(instancesActions.getInstanceLogRequest, getInstanceLogSaga),
    takeLeading(
      instancesActions.getInstanceActionLogsRequest,
      getInstanceActionLogsSaga
    ),
    takeLeading(
      instancesActions.getInstanceMetricsRequest,
      getInstanceMetricsSaga
    )
  ]);
}
